/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import DataGridManager, { DataGridOrTable } from '../DataGrid/DataGridManager';
import { defaultValue } from './CanvasGrid';
import { helpRenders } from './CanvasGrid_Help';
import { numberFormatHelpRenders } from './NumberColumn_Help';
import { StringColumnHelpRenders } from './StringColumn_Help';

// @ts-ignore CanvasGrid
const CanvasGridManager: ComponentManagerFn<'CanvasGrid'> = (
  platform: Platform,
) => {
  const dataGridManager = DataGridManager(
    platform,
    'CanvasGrid' as DataGridOrTable,
  );
  const { helpRenders: _, ...others } = dataGridManager;
  return {
    ...others,
    type: 'CanvasGrid',
    name: 'Canvas Grid',
    isChildAllowed: (page, item, childType, context) => {
      const gridOnlyChildren = (
        [
          'AsyncSelectColumn',
          'DivColumn',
          'TriggerOnRowDoubleClick',
          'MultiSelectLOVColumn',
        ] as ItemType[]
      ).includes(childType);
      if (gridOnlyChildren) {
        return false;
      }
      return dataGridManager.isChildAllowed(page, item, childType, context);
    },
    defaultValue,
    props: {
      ...dataGridManager.props,
    },
    isPropertyVisible(prop, itemData, context): boolean {
      switch (prop) {
        case 'activateOnFirstClick':
        case 'hideRowClickCursorPointer':
          return true;
        case 'allowCopyForReadOnlyCell':
        case 'showRowLines':
        case 'rowBuffer':
        case 'columnBuffer':
        case 'filterable':
        case 'hideHeader':
        case 'showGridFilters':
        case 'allowDownArrowRecordCreation':
        case 'enableSpreadSheetCopy':
          return true;
        default:
          break;
      }
      return dataGridManager.isPropertyVisible(prop, itemData, context);
    },
    replaceableWith: ['DataGrid'],
    description: `**Overview**<br/>
The Canvas Grid is a high-performance variant of the standard Data Grid. While the standard grid renders using HTML elements (DOM), the Canvas Grid renders data directly onto an HTML5 Canvas surface.
This architectural difference makes it significantly faster for scrolling through massive datasets (e.g., 50,000+ rows) but comes with specific trade-offs regarding interactivity and customization. Use this component when raw rendering speed and performance are your top priorities.

**1. Performance vs. Interactivity**<br/>
The choice between Canvas Grid and Data Grid comes down to the specific needs of your page.
* **Rendering Engine:** HTML DOM (Standard) vs. HTML5 Canvas (Canvas Grid).
* **Scrolling Speed:** Standard vs. Extremely Fast.
* **Dataset Size:** Small/Medium vs. Large/Massive.
* **Complex Interactivity:** High (Standard) vs. Low (Canvas Grid - Text/Simple Cells only).
* **Row Editing:** Full Support (Standard) vs. Limited (Canvas Grid).

**2. Configuration & Structure**<br/>
Like the standard grid, the Canvas Grid is a container that requires column definitions to function.
* **Data Source Alias:** Select the Data Store to visualize.
* **Columns:** You must add child Column components (e.g., String Column, Number Column, Date Column) to define what data appears.

**3. Key Limitations (Trade-offs)**<br/>
Because the Canvas Grid draws pixels instead of creating real HTML elements, several features found in the standard Data Grid are not supported or disabled to maintain performance:
* **No Complex Columns:** You cannot use Div Column (custom HTML), Async Select Column, or MultiSelect LOV Column inside a Canvas Grid.
* **No Grid Filters:** The built-in "Filter Row" at the top of the grid is disabled. You must use external filters (like Simple Filter or Smart Search) to control the data.
* **Spreadsheet fill handle:** Enable **Spread Sheet Copy** for the Excel-style fill handle (drag from the bottom-right of the selection to fill). **Allow Record Creation On Hot Key Paste** requires **Allow Hot Key Copy Paste** and an insertable store: it shows a trailing row and creates new records when paste extends past existing rows.
* **No Row Double Click:** The TriggerOnRowDoubleClick event is not supported.

**4. Best Use Cases**<br/>
* **Financial Tickers:** Displaying rapidly changing stock prices or FX rates where update frequency is high.
* **Audit Logs:** Scrolling through extremely long lists of system events.
* **Read-Only Reports:** Large tables of data where the user primarily consumes information rather than editing it.`,
    helpRenders: [
      ...StringColumnHelpRenders,
      ...numberFormatHelpRenders,
      ...helpRenders,
    ],
  };
};

export default CanvasGridManager;
